import { useEffect } from "react";
import { useReducedMotion } from "framer-motion";

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const selectors = [
  [".hm-hero-content", "hero-copy"],
  [".hm-terminal", "hero-panel"],
  [".hm-hero-ghost", "background"],
  [".ea-section-head > p, .hm-head-stack > p, .hm-head-inline > p, .hm-subhead > p", "copy"],
  [".ea-section-head h2, .hm-head-stack h2, .hm-head-inline h2, .hm-subhead h3", "heading"],
  [".ea-cards > .ea-card, .hm-category-card", "card"],
  [".ea-feature", "feature"],
  [".hm-stack-grid > div, .hm-note-grid > div, .hm-process-step", "layer"],
];
const variables = ["--depth-y", "--depth-z", "--depth-rx", "--depth-ry", "--depth-scale", "--depth-opacity", "--pointer-x", "--pointer-y", "--magnet-x", "--magnet-y"];

// A single frame coordinator for native scrolling and pointer interaction.
// Layout is measured on resize/content changes, never in the scroll frame.
export default function useCinematicScroll(rootRef, routeKey) {
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduceMotion || typeof IntersectionObserver === "undefined") return;
    let disposed = false;
    let frame = 0;
    let measurementFrame = 0;
    let viewportHeight = window.innerHeight;
    let mobile = window.innerWidth < 768;
    let pointer = null;
    const records = new Map();
    const active = new Set();
    const interactive = new Set();

    const absoluteTop = (element) => {
      let top = 0;
      for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
      return top;
    };
    const measure = () => {
      viewportHeight = window.innerHeight;
      mobile = window.innerWidth < 768;
      records.forEach((record, element) => {
        record.top = absoluteTop(element);
        record.height = element.offsetHeight;
      });
      schedule();
    };
    const write = (element, values) => {
      for (const [name, value] of Object.entries(values)) element.style.setProperty(name, value);
    };
    const render = () => {
      frame = 0;
      if (disposed) return;
      const scrollY = window.scrollY;
      active.forEach((element) => {
        const record = records.get(element);
        if (!record) return;
        const relativeTop = record.top - scrollY;
        const range = viewportHeight * 0.64 + Math.min(record.height * 0.18, 90);
        // Smooth progress follows scrolling exactly, including reversing direction.
        const progress = clamp((viewportHeight - relativeTop - record.stagger) / range);
        const eased = 1 - (1 - progress) ** 3;
        const entrance = 1 - eased;
        const exit = clamp((-relativeTop - record.height * 0.5) / viewportHeight);
        const strength = mobile ? 0.35 : 1;
        let y = entrance * (record.kind === "card" ? 12 : 16) * strength - exit * 10 * strength;
        let z = -entrance * 55 * strength;
        let rx = entrance * (record.kind === "card" ? 3 : 2) * strength;
        let ry = record.kind === "feature" ? entrance * record.side * 3 * strength : 0;
        let scale = 1 - entrance * 0.025 * strength;
        if (record.kind.startsWith("hero")) {
          const travel = clamp((scrollY - record.top) / Math.max(record.height, 1));
          y = travel * (record.kind === "hero-panel" ? -38 : -16) * strength;
          rx = travel * (record.kind === "hero-panel" ? 5 : -1.5) * strength;
          ry = travel * (record.kind === "hero-panel" ? -4 : 0) * strength;
          z = -travel * 35 * strength;
          scale = 1 - travel * 0.035 * strength;
        } else if (record.kind === "background") {
          y = clamp(scrollY - record.top, -viewportHeight, viewportHeight) * 0.12 * strength;
          z = 0; rx = 0; ry = 0; scale = 1;
        }
        write(element, {
          "--depth-y": `${y.toFixed(2)}px`, "--depth-z": `${z.toFixed(2)}px`,
          "--depth-rx": `${rx.toFixed(2)}deg`, "--depth-ry": `${ry.toFixed(2)}deg`,
          "--depth-scale": scale.toFixed(4),
          "--depth-opacity": record.kind.startsWith("hero") || record.kind === "background" ? "1" : (0.5 + eased * 0.5).toFixed(3),
        });
      });
      if (pointer) {
        write(pointer.element, pointer.magnetic ? {
          "--magnet-x": `${(pointer.x * 5).toFixed(2)}px`, "--magnet-y": `${(pointer.y * 4).toFixed(2)}px`,
        } : {
          "--pointer-x": `${(-pointer.y * 2).toFixed(2)}deg`, "--pointer-y": `${(pointer.x * 3).toFixed(2)}deg`,
        });
        pointer = null;
      }
    };
    function schedule() { if (!frame && !disposed) frame = requestAnimationFrame(render); }
    const scheduleMeasurement = () => {
      if (!measurementFrame && !disposed) measurementFrame = requestAnimationFrame(() => { measurementFrame = 0; measure(); });
    };
    const visibility = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting ? active.add(entry.target) : active.delete(entry.target));
      schedule();
    }, { rootMargin: "160px 0px" });
    const register = () => {
      selectors.forEach(([selector, kind]) => {
        root.querySelectorAll(`.editorial-ai ${selector.split(", ").join(", .editorial-ai ")}`).forEach((element, index) => {
          if (records.has(element) || element.closest(".cl-page, .hm-page")) return;
          // Existing Framer wrappers retain ownership of their own transforms.
          if (element.style.transform && kind !== "hero-panel") return;
          element.dataset.depthKind = kind;
          records.set(element, { kind, top: absoluteTop(element), height: element.offsetHeight, side: index % 2 ? -1 : 1, stagger: kind === "card" ? (Array.from(element.parentElement.children).indexOf(element) % 3) * 28 : 0 });
          visibility.observe(element);
        });
      });
      root.querySelectorAll(".editorial-ai .ea-primary").forEach((element) => {
        if (element.closest(".hm-page")) return;
        element.classList.add("cinematic-magnetic"); interactive.add(element);
      });
      // Release nodes replaced by a lazy route or dynamic content update.
      records.forEach((_, element) => {
        if (!root.contains(element)) { visibility.unobserve(element); active.delete(element); records.delete(element); }
      });
      measure();
    };
    const onPointerMove = (event) => {
      if (mobile || event.pointerType !== "mouse" || !window.matchMedia("(hover:hover) and (pointer:fine)").matches) return;
      const element = event.target.closest('[data-depth-kind="card"], .cinematic-magnetic');
      if (!element || !root.contains(element)) return;
      const bounds = element.getBoundingClientRect();
      pointer = { element, magnetic: element.classList.contains("cinematic-magnetic"), x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1) * 2 - 1, y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1) * 2 - 1 };
      interactive.add(element); schedule();
    };
    const onPointerOut = (event) => {
      const element = event.target.closest('[data-depth-kind="card"], .cinematic-magnetic');
      if (!element || element.contains(event.relatedTarget)) return;
      if (pointer?.element === element) pointer = null;
      write(element, { "--pointer-x": "0deg", "--pointer-y": "0deg", "--magnet-x": "0px", "--magnet-y": "0px" });
    };
    root.classList.add("cinematic-scroll");
    register();
    const mutations = new MutationObserver((entries) => {
      if (entries.some((entry) => Array.from(entry.addedNodes).some((node) => node.nodeType === 1) || Array.from(entry.removedNodes).some((node) => node.nodeType === 1))) register();
    });
    mutations.observe(root, { childList: true, subtree: true });
    const resize = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasurement) : null;
    resize?.observe(root);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", scheduleMeasurement, { passive: true });
    root.addEventListener("pointermove", onPointerMove, { passive: true });
    root.addEventListener("pointerout", onPointerOut, { passive: true });
    document.fonts?.ready.then(() => { if (!disposed) scheduleMeasurement(); });
    return () => {
      disposed = true; cancelAnimationFrame(frame); cancelAnimationFrame(measurementFrame);
      visibility.disconnect(); mutations.disconnect(); resize?.disconnect();
      window.removeEventListener("scroll", schedule); window.removeEventListener("resize", scheduleMeasurement);
      root.removeEventListener("pointermove", onPointerMove); root.removeEventListener("pointerout", onPointerOut);
      new Set([...records.keys(), ...interactive]).forEach((element) => {
        delete element.dataset.depthKind; element.classList.remove("cinematic-magnetic");
        variables.forEach((name) => element.style.removeProperty(name));
      });
      root.classList.remove("cinematic-scroll");
    };
  }, [rootRef, routeKey, reduceMotion]);
}
